import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import request from '../utils/request';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-indigo-100 text-indigo-700',
  needs_info: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  suspended: 'bg-red-100 text-red-700',
  deactivated: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  needs_info: 'Needs Info',
  approved: 'Approved',
  rejected: 'Rejected',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

const FILTER_STATUSES = ['', 'submitted', 'under_review', 'needs_info', 'approved', 'rejected', 'suspended'];

export default function ProviderQueueScreen() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [page, setPage] = useState(1);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await request.get('/admin/providers', { params });
      setProviders(data.providers || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Provider Review Queue</h1>
            <p className="text-sm text-zinc-500 mt-1">{total} provider{total !== 1 ? 's' : ''} found</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {FILTER_STATUSES.map((s) => (
            <button
              key={s || 'all'}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              {s ? STATUS_LABELS[s] || s : 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-20 text-zinc-400">
            <p className="text-lg font-medium">No providers found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Provider</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Business</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Services</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Markets</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {providers.map((p) => (
                  <tr
                    key={p._id}
                    onClick={() => navigate(`/providers/${p._id}`)}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-zinc-900">{p.firstName} {p.lastName}</div>
                      <div className="text-sm text-zinc-500">{p.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-600">{p.businessName || '\u2014'}</td>
                    <td className="px-6 py-4 text-sm text-zinc-600">
                      {p.serviceCategories?.map((sc) => sc.serviceCategoryId?.displayName || sc.serviceCategoryId?.name).filter(Boolean).join(', ') || '\u2014'}
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-600">
                      {p.markets?.map((m) => m.marketId?.name).filter(Boolean).join(', ') || '\u2014'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-500">
                      {p.submittedAt ? new Date(p.submittedAt).toLocaleDateString() : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 50 && (
          <div className="flex justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-zinc-100 text-zinc-600 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-zinc-500">Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * 50 >= total}
              className="px-4 py-2 rounded-lg bg-zinc-100 text-zinc-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
